import {ValueObject} from '@s-ui/domain'

import {MatchUserAlertsValueObject} from './MatchUserAlertsValueObject.js'

export class MatchesListUserAlertsValueObject extends ValueObject {
  #items
  #search
  #config

  #cache = new Map()

  static #CACHE_KEYS = {
    ITEMS: 'items',
    MISSED: 'missed',
    RECENT: 'recent'
  }

  #memoize(key, fn) {
    if (!this.#cache.has(key)) {
      this.#cache.set(key, fn())
    }
    return this.#cache.get(key)
  }

  constructor({items, search, config}) {
    super()
    this.#items = items
    this.#search = search
    this.#config = config
  }

  static create({items, search, config}) {
    return new MatchesListUserAlertsValueObject({
      items: items.map(item => MatchUserAlertsValueObject.create(item)),
      search,
      config
    })
  }

  get items() {
    return this.#memoize(MatchesListUserAlertsValueObject.#CACHE_KEYS.ITEMS, () =>
      this.#items.map(item => item.getValue())
    )
  }

  getAllMatches() {
    return this.items
  }

  getMissedMatches() {
    return this.#memoize(MatchesListUserAlertsValueObject.#CACHE_KEYS.MISSED, () => {
      const filtered = this.#items.filter(item => item.getIsMissed())
      return this.#sortItems(filtered)
    })
  }

  getRecentMatches() {
    return this.#memoize(MatchesListUserAlertsValueObject.#CACHE_KEYS.RECENT, () => {
      const filtered = this.#items.filter(item => item.getIsRecent())
      return this.#sortItems(filtered)
    })
  }

  #sortItems(items) {
    if (!this.#search?.sortBy || !this.#search?.sortOrder || !this.#config) {
      return items.map(item => item.getValue())
    }

    const {SORTABLE_ALERT_MATCH_FIELDS = {}} = this.#config.get('alerts')
    const ORDER = this.#config.get('ORDER')
    const fieldAccessor = SORTABLE_ALERT_MATCH_FIELDS[this.#search.sortBy]

    if (!fieldAccessor) {
      return items.map(item => item.getValue())
    }

    const orderFactor = this.#search.sortOrder.toLowerCase() === ORDER.DESC.toLowerCase() ? -1 : 1

    const sorted = [...items].sort((a, b) => {
      const valueA = fieldAccessor(a)
      const valueB = fieldAccessor(b)

      // Null handling: push nulls to end
      if (valueA == null && valueB == null) return 0
      if (valueA == null) return 1
      if (valueB == null) return -1

      return (valueA - valueB) * orderFactor
    })

    return sorted.map(item => item.getValue())
  }

  hasMatches() {
    return this.items.length > 0
  }

  hasMissedItems() {
    return this.getMissedMatches().length > 0
  }

  hasRecentItems() {
    return this.getRecentMatches().length > 0
  }

  getTotalMatches() {
    return this.items.length
  }

  getTotalMissedMatches() {
    return this.getMissedMatches().length
  }

  getTotalRecentMatches() {
    return this.getRecentMatches().length
  }

  getValue() {
    return {
      items: this.items
    }
  }

  toJSON() {
    return {
      items: this.#items.map(item => item.toJSON())
    }
  }
}
